import { useState, useCallback, useEffect, createContext, useContext } from "react";
import { Joyride, Step, EventData, Controls, STATUS, ACTIONS, type Options } from "react-joyride";
import { REVEAL_TOUR_NAV_TARGETS_EVENT } from "@/components/layout/nav-config";

const TOUR_COMPLETE_KEY = "plate-os-tour-complete";
const TOUR_ENABLED_KEY = "plate-os-tour-enabled";

interface TourContextValue {
  startTour: () => void;
  startPageTour: (page?: string) => void;
  isTourActive: boolean;
  tourEnabled: boolean;
  setTourEnabled: (enabled: boolean) => void;
}

const TourContext = createContext<TourContextValue>({
  startTour: () => {},
  startPageTour: () => {},
  isTourActive: false,
  tourEnabled: true,
  setTourEnabled: () => {},
});

export function useTour() {
  return useContext(TourContext);
}

export const DESKTOP_NAV_STEPS: Step[] = [
  {
    target: "[data-tour='sidebar-nav']",
    title: "Welcome to Plate OS",
    content: "This sidebar is your hub — log food, check scores, and optimize your nutrition all from here.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-plate-talk']",
    title: "Plate Talk",
    content: "Type, photo, barcode, or voice — log any meal and the AI breaks down 157+ nutrients instantly so you know exactly what you're getting.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-dashboard']",
    title: "Dashboard",
    content: "See the real-time effect of what you eat — macros, Gut IQ, hydration, TEF calorie burn, and your logging streak all update as you log.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-grocery']",
    title: "Grocery Builder",
    content: "Paste your shopping list before you buy — the AI scores every item, flags nutrient gaps, and suggests smarter swaps like quinoa over white rice for 3x the fiber.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-plate-log']",
    title: "Plate Log",
    content: "Structure meals into Breakfast, Lunch, Dinner, and Snacks with gram-level portions — spreading protein across meals boosts muscle synthesis by up to 25%.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-foods']",
    title: "Food Database",
    content: "Browse 204+ foods with full profiles — when Metrics shows you're low on a nutrient, search here to find the richest food sources.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-evidence']",
    title: "Evidence Lab",
    content: "See the gut science behind every food — Gut Impact scores, NOVA processing levels, microbiome bacteria data, and powerhouse rankings to make evidence-backed choices.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-metrics']",
    title: "Metric Explorer",
    content: "Track 157+ nutrients against your daily targets — spot deficiencies causing fatigue or brain fog that simple calorie counting misses.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-grid']",
    title: "Daily Grid",
    content: "A color-coded monthly heatmap — red columns reveal chronic nutrient gaps, green rows show your best days to replicate.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-smart-swap']",
    title: "Smart Swap IQ",
    content: "Find healthier alternatives for foods you already eat — small swaps like Greek yogurt over regular double your protein without changing your routine.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-glossary']",
    title: "Glossary",
    content: "Look up any nutrient to learn what it does, why it matters, and which foods are richest in it.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-help']",
    title: "Help & FAQ",
    content: "21+ searchable FAQs with step-by-step guides — plus you can relaunch this tour anytime.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-profile']",
    title: "Profile",
    content: "Set your body metrics, nutrition phase, and dietary restrictions — these drive all your personalized calorie and macro targets across the app.",
    placement: "right",
    skipBeacon: true,
  },
];

const mobileNavSteps: Step[] = [
  {
    target: "[data-tour='mobile-nav']",
    title: "Navigation",
    content: "Your main tabs — log food, check your Dashboard scores, build grocery lists, and tap More for advanced tools.",
    placement: "top",
    skipBeacon: true,
  },
  {
    target: "[data-tour='mobile-nav-plate-talk']",
    title: "Plate Talk",
    content: "Type, snap a photo, scan a barcode, or speak — the AI logs your meal and calculates 157+ nutrients instantly.",
    placement: "top",
    skipBeacon: true,
  },
  {
    target: "[data-tour='mobile-nav-dashboard']",
    title: "Dashboard",
    content: "Your nutrition scoreboard — macros, Gut IQ, hydration, and calorie burn update in real-time as you log meals.",
    placement: "top",
    skipBeacon: true,
  },
  {
    target: "[data-tour='mobile-nav-grocery']",
    title: "Grocery Builder",
    content: "Paste your shopping list and AI audits every item — scores, nutrient gaps, and smarter swap suggestions before you buy.",
    placement: "top",
    skipBeacon: true,
  },
  {
    target: "[data-tour='mobile-nav-plate-log']",
    title: "Plate Log",
    content: "Log meals by slot with gram-level precision — the AI can auto-fill any meal to hit your exact targets.",
    placement: "top",
    skipBeacon: true,
  },
  {
    target: "[data-tour='mobile-nav-more']",
    title: "More Tools",
    content: "Access Metrics, Evidence Lab, Daily Grid, Smart Swap, Glossary, and Profile settings here.",
    placement: "top",
    skipBeacon: true,
  },
];

const dashboardSteps: Step[] = [
  {
    target: "[data-tour='dashboard-header']",
    title: "Time Views",
    content: "Switch Day, Week, or Month to spot patterns — a single bad day is noise, but a week of low protein reveals a real gap to fix.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='dashboard-neural']",
    title: "Neural Engine",
    content: "Your streak tracker — longer streaks mean more data, which means the AI learns your habits and gives personalized recommendations instead of generic advice.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='dashboard-macros']",
    title: "Macro Overview",
    content: "Calories, protein, carbs, fat, fiber, and TEF score — low protein hurts recovery, low fiber hurts gut health, and a higher TEF means your body burns more calories digesting food.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='dashboard-hydration']",
    title: "Hydration Intelligence",
    content: "Tracks water from glasses AND food content — even mild dehydration cuts focus and energy, while the electrolyte panel shows if cramps or fatigue come from mineral imbalances.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='dashboard-gutiq']",
    title: "Gut IQ",
    content: "Your microbiome score from -100 to +100 — ultra-processed foods drag it down causing inflammation, while whole foods and fermented foods push it up supporting immunity.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='dashboard-portion']",
    title: "Portion Density",
    content: "Visual map of what you ate sized by portion — if one food dominates your plate, you lack variety and likely have nutrient gaps.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='dashboard-status']",
    title: "3D Status",
    content: "See all 157+ nutrients as green (adequate), red (deficient), or orange (excess) — your goal is to grow the green segment by fixing gaps found in Metric Explorer.",
    placement: "bottom",
    skipBeacon: true,
  },
];

const plateTalkSteps: Step[] = [
  {
    target: "[data-tour='plate-talk-header']",
    title: "Chat History",
    content: "All past conversations saved by category — revisit any meal plan, analysis, or AI recommendation from previous days.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='plate-talk-input']",
    title: "Log Your Meal",
    content: "Type what you ate, snap a photo, scan a barcode, or use voice — the easier logging is, the more consistent you'll be, and consistency is what drives accurate AI coaching.",
    placement: "top",
    skipBeacon: true,
  },
  {
    target: "[data-tour='plate-talk-commands']",
    title: "Command Matrix",
    content: "Tap ⌘ for AI actions — generate meal plans that fill YOUR nutrient gaps, get smart swap suggestions, run gut health analysis, or rearrange meals for better nutrient timing.",
    placement: "top",
    skipBeacon: true,
  },
  {
    target: "[data-tour='plate-talk-nav']",
    title: "Quick Navigation",
    content: "Jump to any section without leaving your conversation — when the AI says you're low on fiber, jump straight to Foods to find sources.",
    placement: "top",
    skipBeacon: true,
  },
];

const plateLogSteps: Step[] = [
  {
    target: "[data-tour='plate-log-header']",
    title: "Plate Log",
    content: "Your structured food diary — log meals by slot and watch daily totals update in real-time so you can course-correct before the day ends.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='plate-log-summary']",
    title: "Daily Summary",
    content: "Live progress bars for calories, protein, carbs, fat, and fiber against YOUR targets — if it's 2pm and protein is at 20%, you know dinner needs to be protein-heavy.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='plate-log-water']",
    title: "Water Tracker",
    content: "Tap glasses to log water — the app also auto-counts water from food (watermelon is 92% water), so you see true total hydration.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='plate-log-slots']",
    title: "Meal Slots",
    content: "Breakfast, Lunch, Dinner, Snacks, and custom slots — balance protein across meals instead of loading it all at dinner for better absorption and energy.",
    placement: "bottom",
    skipBeacon: true,
  },
];

const grocerySteps: Step[] = [
  {
    target: "[data-tour='grocery-input']",
    title: "Grocery Input",
    content: "Paste your shopping list and hit Analyze — catch nutrient gaps at the store before they reach your kitchen. Use AI Command List to auto-generate a list from your actual deficiencies.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='grocery-results']",
    title: "Analysis Results",
    content: "Every item scored with health grade, Gut IQ impact, NOVA processing level, and specific swap suggestions — small upgrades each trip compound into major improvements over weeks.",
    placement: "top",
    skipBeacon: true,
  },
];

const evidenceSteps: Step[] = [
  {
    target: "[data-tour='evidence-tabs']",
    title: "Evidence Lab",
    content: "Five research tabs — Gut Impact scores foods -100 to +100, NOVA shows processing levels, Microbiome reveals which bacteria each food feeds, and Powerhouse ranks the most nutrient-dense options.",
    placement: "bottom",
    skipBeacon: true,
  },
];

const profileSteps: Step[] = [
  {
    target: "[data-tour='profile-body']",
    title: "Body Metrics",
    content: "Your height, weight, and body fat set all calorie and macro targets — keep these updated because even a 5kg difference shifts your daily target by 150+ calories.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: "[data-tour='profile-preferences']",
    title: "Preferences",
    content: "Choose your color theme, language (20+), and toggle Lite Mode for a simplified view — a tool you enjoy using is one you'll use consistently.",
    placement: "bottom",
    skipBeacon: true,
  },
];

const fabSteps: Step[] = [
  {
    target: "[data-tour='audio-fab']",
    title: "Quick Actions",
    content: "Scan barcodes instantly, toggle voice responses for hands-free use, hear your daily stats spoken aloud, or relaunch this tour — all from one floating button.",
    placement: "left",
    skipBeacon: true,
  },
];

function isMobileView() {
  return typeof window !== "undefined" && window.innerWidth < 768;
}

export function getDataTourTargetId(step: Step): string | undefined {
  if (typeof step.target !== "string") return undefined;
  return step.target.match(/^\[data-tour=['"]([^'"]+)['"]\]$/)?.[1];
}

function filterVisibleSteps(steps: Step[]): Step[] {
  return steps.filter((step) => {
    if (typeof step.target === "string") {
      return document.querySelector(step.target) !== null;
    }
    return true;
  });
}

function revealDesktopNavTargets(steps: Step[], onReady: () => void) {
  const targetIds = steps
    .map(getDataTourTargetId)
    .filter((id): id is string => id?.startsWith("nav-") ?? false);

  if (targetIds.length === 0 || typeof window === "undefined") {
    onReady();
    return;
  }

  window.dispatchEvent(
    new CustomEvent(REVEAL_TOUR_NAV_TARGETS_EVENT, { detail: { targetIds } }),
  );

  // The sidebar needs a render pass to mount links from newly-opened groups
  // before the visibility filter below decides which Joyride steps can run.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(onReady);
  });
}

function normalizePath(raw: string): string {
  return raw.split("?")[0].split("#")[0] || "/";
}

function getPageSteps(page?: string): Step[] {
  const path = normalizePath(page || (typeof window !== "undefined" ? window.location.pathname : "/"));

  const pageStepsMap: Record<string, Step[]> = {
    "/dashboard": dashboardSteps,
    "/": dashboardSteps,
    "/plate-talk": plateTalkSteps,
    "/plate-log": plateLogSteps,
    "/grocery": grocerySteps,
    "/evidence": evidenceSteps,
    "/profile": profileSteps,
  };

  const pageSpecific = pageStepsMap[path] || [];
  return [...pageSpecific, ...fabSteps];
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [tourEnabled, setTourEnabledState] = useState(() => {
    const stored = localStorage.getItem(TOUR_ENABLED_KEY);
    return stored === null ? true : stored === "true";
  });

  const setTourEnabled = useCallback((enabled: boolean) => {
    setTourEnabledState(enabled);
    localStorage.setItem(TOUR_ENABLED_KEY, String(enabled));
    if (!enabled) {
      setRun(false);
    }
  }, []);

  const startTour = useCallback(() => {
    const isMobile = isMobileView();
    const navSteps = isMobile ? mobileNavSteps : DESKTOP_NAV_STEPS;
    const pageSteps = getPageSteps();
    const allSteps = [...navSteps, ...pageSteps];
    const startVisibleSteps = () => {
      const visible = filterVisibleSteps(allSteps);
      if (visible.length > 0) {
        setSteps(visible);
        setRun(true);
      }
    };

    if (isMobile) {
      startVisibleSteps();
    } else {
      revealDesktopNavTargets(DESKTOP_NAV_STEPS, startVisibleSteps);
    }
  }, []);

  const startPageTour = useCallback((page?: string) => {
    const pageSteps = getPageSteps(page);
    const visible = filterVisibleSteps(pageSteps);
    if (visible.length > 0) {
      setSteps(visible);
      setRun(true);
    }
  }, []);

  const handleEvent = useCallback((data: EventData, _controls: Controls) => {
    const { status, action } = data;
    if (
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      action === ACTIONS.CLOSE
    ) {
      setRun(false);
      localStorage.setItem(TOUR_COMPLETE_KEY, "true");
    }
  }, []);

  return (
    <TourContext.Provider value={{ startTour, startPageTour, isTourActive: run, tourEnabled, setTourEnabled }}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        continuous
        scrollToFirstStep
        onEvent={handleEvent}
        options={{
          overlayClickAction: false,
          zIndex: 10000,
          primaryColor: "hsl(var(--primary))",
          backgroundColor: "hsl(var(--card))",
          textColor: "hsl(var(--card-foreground))",
          overlayColor: "rgba(0, 0, 0, 0.6)",
        } as unknown as Partial<Options>}
        locale={{
          back: "Back",
          close: "Close",
          last: "Finish Tour",
          next: "Next",
          skip: "Skip Tour",
        }}
      />
    </TourContext.Provider>
  );
}

export function useTourAutoLaunch() {
  const { startTour, tourEnabled } = useTour();

  useEffect(() => {
    if (!tourEnabled) return;
    const completed = localStorage.getItem(TOUR_COMPLETE_KEY);
    if (!completed) {
      const timer = setTimeout(() => {
        startTour();
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [startTour, tourEnabled]);
}

export function isTourCompleted(): boolean {
  return localStorage.getItem(TOUR_COMPLETE_KEY) === "true";
}

export function resetTourCompletion() {
  localStorage.removeItem(TOUR_COMPLETE_KEY);
}
